// Angular imports
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// 3rd party imports
import { DxProgressBarComponent, DxValidationGroupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// Component imports
import { CCMessageBoxIcon, IconConstants, CCPasswordHelperService } from 'cc-common';
import { RecaptchaBaseComponent } from '../_base/recaptcha-base.component';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';

// Constant imports
import { Constants } from '../../_constants/constants';
import { MessageInterval } from '../../_constants/messages-intervals';

// Model imports
import { ResultStatusDto } from '../../_models/result-status-dto';
import { ResetPasswordParametersDto } from '../../_models/reset-password-parameters-dto';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent extends RecaptchaBaseComponent implements OnInit {
    @ViewChild('resetPasswordValidationGroup', { static: false })
    resetPasswordValidationGroup: DxValidationGroupComponent;
    @ViewChild('passStrengthSuccess', { static: false })
    passStrengthSuccess: DxProgressBarComponent;

    isSmallScreen = false;
    loadingVisible = false;
    passwordMode: string;
    passwordShowButton: object;
    passwordHideButton: object;
    passwordStrength = 0;
    resetPasswordParameterDto = new ResetPasswordParametersDto();
    passwordSpecialCharactersAllowable = Constants.passwordSpecialCharactersAllowable;
    backToLoginIcon = IconConstants.BACK_TO_LOGIN;
    submitIcon = IconConstants.SUBMIT;

    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly userService: UserService,
        private readonly screenService: ScreenService,
        private readonly passwordHelper: CCPasswordHelperService,
        protected readonly errorService: ServerErrorService
    ) {
        super(errorService);
    }

    ngOnInit() {
        this.route.queryParams.subscribe((params) => {
            const token = params.token;
            if (!!token && token.length > 0) {
                this.resetPasswordParameterDto.token = encodeURIComponent(token);
            } else {
                this.router.navigate(['user', 'login']);
            }
        });
        this.passwordMode = 'password';
        this.passwordShowButton = {
            icon: 'fas fa-eye',
            type: 'default',
            stylingMode: 'text',
            hint: 'Show Password',
            onClick: () => {
                this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
            }
        };
        this.passwordHideButton = {
            icon: 'fas fa-eye-slash',
            type: 'default',
            stylingMode: 'text',
            hint: 'Hide Password',
            onClick: () => {
                this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
            }
        };
        this.updateScreenSize();
        this.screenService.changed.subscribe((next) => {
            this.updateScreenSize();
        });
    }

    btnResetPasswordClick(event: any): void {
        const validator = this.resetPasswordValidationGroup.instance.validate();
        if (validator.isValid) {
            if (this.resetPasswordParameterDto.newPassword.length < 8) {
                notify('Password must have at least 8 characters.', 'error', MessageInterval.defaultErrorMsgInterval);
            } else if (this.passwordHelper.hasSpecialCharacter(this.resetPasswordParameterDto.newPassword) === false) {
                notify(
                    'Your password must contain atleast one special character',
                    'error',
                    MessageInterval.defaultErrorMsgInterval
                );
            } else if (this.passwordStrength < 3) {
                notify(
                    `Your password must meet 3 of 4 criteria: lower case, upper case, number, special character (${Constants.passwordSpecialCharactersAllowable}).`,
                    'error',
                    MessageInterval.defaultErrorMsgInterval
                );
            } else if (this.isValidRecaptcha()) {
                this.resetPasswordParameterDto.recaptchaToken = this.recaptchaToken;
                this.resetPasswordParameterDto.confirmNewPassword = this.resetPasswordParameterDto.newPassword;
                this.doResetPassword().then(() => this.resetRecaptcha());
            }
        }
    }

    btnBackToLogin(event: any): void {
        this.router.navigate(['user', 'login']);
    }

    onPasswordChanged(event): void {
        const passvalue = event.value;
        this.updatePasswordStrength(passvalue);
    }

    updatePasswordStrength(passValue: string) {
        this.passwordStrength = this.passwordHelper.getPasswordStrength(passValue);
        this.passStrengthSuccess.value = this.passwordStrength;
    }

    passwordComparison = () => {
        return this.resetPasswordParameterDto.newPassword;
    };

    getPasswordStrengthText(value: number): string {
        return `Password Strength ${value * 100}%`;
    }

    private doResetPassword(): Promise<void> {
        return new Promise((resolve) => {
            this.loadingVisible = true;
            this.userService.resetPassword(this.resetPasswordParameterDto).subscribe(
                (result: ResultStatusDto) => {
                    resolve();
                    this.loadingVisible = false;
                    if (!!result && result.isSuccess) {
                        this.messageBox.showWithFixedWidthAndIcon(
                            CCMessageBoxIcon.success,
                            'Success',
                            `Your password has been reset successfully.`,
                            320
                        );
                        this.btnBackToLogin(null);
                    } else {
                        this.messageBox.showWithFixedWidthAndIcon(
                            CCMessageBoxIcon.error,
                            'Error',
                            !!result && !!result.errorMessage
                                ? result.errorMessage
                                : `Your password reset failed. Try again later`,
                            320
                        );
                    }
                },
                (error) => {
                    resolve();
                    this.loadingVisible = false;
                    this.processApiError(error);
                }
            );
        });
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
    }
}
